import logger from '@clamjs/logger';
import { $ } from 'execa';

import { runShell } from './tools';

export interface NodeEnv {
  pnpmVersion: string; // pnpm 版本
  tnpmVersion: string; // tnpm 版本
  npmVersion: string; // npm 版本
  nodeVersion: string; // node 版本
}

export interface GitEnv {
  branch: string;
  userNick: string;
  userEmail: string;
}

const cachedEnvs: Partial<{
  node: string;
  npm: string;
  tnpm: string;
  pnpm: string;
  git: string;
}> = {};

const getVersionFactory = (fn: () => string, cacheKey: keyof typeof cachedEnvs) => {
  return (options: { noCache?: boolean; silent?: boolean } = {}): string => {
    if (cachedEnvs[cacheKey] && !options.noCache) {
      return cachedEnvs[cacheKey] || '';
    }

    try {
      const value = fn();
      cachedEnvs[cacheKey] = value;
      return value;
    } catch (error) {
      if (!options.silent) {
        throw error;
      } else {
        return '';
      }
    }
  };
};

export const getNodeVersion = getVersionFactory(() => {
  return process.version;
}, 'node');

export const getNpmVersion = getVersionFactory(() => {
  const { stdout } = $.sync`npm -v`;
  return stdout;
}, 'npm');

export const getTnpmVersion = getVersionFactory(() => {
  const { stdout } = $.sync`tnpm -v`;
  return stdout?.match(/tnpm@(\d+\.\d+\.\d+)/u)?.[1] || '';
}, 'tnpm');

export const getPnpmVersion = getVersionFactory(() => {
  const { stdout } = $.sync`pnpm -v`;
  return stdout;
}, 'pnpm');

export const getGitVersion = getVersionFactory(() => {
  // TODO 目前 pipe`` 不好使，所以先用 shell，等官方修复后替换之
  const { stdout } = $({ shell: true }).sync`git -v | awk '{print $3}'`;
  return stdout;
}, 'git');

/** @returns Node相关环境信息 */
export const getNodeEnv = (): NodeEnv => {
  return {
    nodeVersion: getNodeVersion(),
    npmVersion: getNpmVersion(),
    tnpmVersion: getTnpmVersion(),
    pnpmVersion: getPnpmVersion(),
  };
};

/** @returns Git相关环境信息 */
export const getGitEnv = (): GitEnv => {
  return {
    branch: runShell('git rev-parse --abbrev-ref HEAD'),
    userNick: runShell('git config user.name'),
    userEmail: runShell('git config --global user.email'),
  };
};

export const PUBLISH_ENV = {
  pre: 'prerelease',
  prod: 'production',
  default: 'prerelease',
};

export const getAssetsCdnHost = () => {
  const env = process.env.PUBLISH_ENV;
  const notSet = env === undefined || env === '';
  if (env === PUBLISH_ENV.prod || (notSet && PUBLISH_ENV.default === PUBLISH_ENV.prod)) {
    return 'g.alicdn.com';
  } else if (env === PUBLISH_ENV.pre || (notSet && PUBLISH_ENV.default === PUBLISH_ENV.pre)) {
    return 'dev.g.alicdn.com';
  }
  logger.info('环境变量 PUBLISH_ENV 未设置或不合法，默认采用线上环境');
  return 'g.alicdn.com';
};

/** 判断当前是在云构建环境 */
export const isCloudBuildEnv = () => {
  // https://builder.alibaba-inc.com/document?slug=env-intro
  return process.env.BUILD_ENV === 'cloud';
};

export const getCloudBuilderPkgPath = () => {
  return process.env.BUILD_BUILDER_DIR || '';
};
